import { GridColDef } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import { StackedTextCell, TopLineTypography } from '@retail/components';
import { BaseMgSupplierCampaignItem } from '@retail/campaign/types';
import { useAppTFunction } from '@retail/app/i18n';
import { useMgItemAndExternalNumberColumn } from '@retail/products/components';

const baseColumnProps: Partial<GridColDef> = {
  width: 140,
  headerAlign: 'left',
  align: 'left',
  sortable: false,
};

export interface ItemInfoColumnsProps {
  externalSourceType: 'NOBB' | 'FINFO';
}

export const useItemInfoColumns = ({ externalSourceType }: ItemInfoColumnsProps) => {
  const t = useAppTFunction();
  const mgItemAndExternalNumberColumn =
    useMgItemAndExternalNumberColumn<BaseMgSupplierCampaignItem>({
      columnProps: baseColumnProps,
      externalItemSource: externalSourceType,
    });

  return useMemo(() => {
    const getItemIdsColumn = () => mgItemAndExternalNumberColumn;

    const getItemNameColumn = (): GridColDef<BaseMgSupplierCampaignItem> => ({
      ...baseColumnProps,
      field: 'itemText',
      headerName: t('myAssortment.columns.itemText'),
      width: 300,
      renderCell: ({ row }) => (
        <StackedTextCell
          nullableBottomLine
          topLine={row.primaryText}
          bottomLine={row.secondaryText}
          slotProps={{ topLine: { maxLines: 2 }, bottomLine: { maxLines: 1 } }}
        />
      ),
    });

    const getSupplierColumn = (): GridColDef<BaseMgSupplierCampaignItem> => ({
      ...baseColumnProps,
      width: 200,
      field: 'supplier',

      headerName: t('myAssortment.columns.supplier'),
      renderCell: ({ row }) => (
        <StackedTextCell
          topLine={
            <TopLineTypography variant="subtitle2" isInline>
              {row.supplier.name}
            </TopLineTypography>
          }
          bottomLine={row.supplier.id}
        />
      ),
    });

    return { getItemIdsColumn, getItemNameColumn, getSupplierColumn };
  }, [mgItemAndExternalNumberColumn, t]);
};
